/**
 * Single source of truth for site content.
 *
 * ── The model ────────────────────────────────────────────────────────────
 *   FBS (Flexi Business Solutions), THE COMPANY. Not a product name.
 *   FBS, THE PLATFORM, the flagship and the full package. Its suites
 *              (POS & Inventory, Back office, Marketplace) are parts of one
 *              integrated system, not separate products. They share one stock
 *              figure and one ledger.
 *   Client builds, systems built for specific clients and named with their
 *              permission: MERGRAFAV GLOBAL TMS, Towa Associates ERP.
 *
 * ── Language rule (owner instruction) ────────────────────────────────────
 *   No unverifiable numbers and no superlatives. The platform is IN BETA and
 *   the site says so plainly. Every figure that appears inside a product view
 *   is an illustrative example and is labelled as such wherever it shows.
 *   When there is real measured data worth publishing, it goes here.
 */

export const STATUS = {
  badge: 'In beta',
  headline: 'In beta, with early deployments in Nigeria.',
  /**
   * Covers NAMES as well as figures. Supplier names in the example windows are
   * deliberately generic ("Wholesaler A") rather than invented brands, because
   * a plausible-sounding invented company name is very likely to collide with a
   * real business, an early draft used three real Nigerian pharmaceutical
   * companies without meaning to.
   */
  figuresNote: 'Illustrative example data: placeholder names and figures, not live customer data.',
};

export const COMPANY = {
  name: 'Flexi Business Solutions',
  short: 'FBS',
  legal: 'Flexi Business Solutions Ltd',
  location: 'Lagos, Nigeria',
  blurb:
    'We build business software for African enterprises, one integrated platform for selling, stocking, buying and accounting, plus custom systems for individual clients.',
};

/**
 * Replaces the old "500+ / 99.9% / 20+ / 4" stat band. Same visual slot, but
 * every line is a fact about what the product IS rather than a claim about
 * how well it has performed.
 */
export const CAPABILITIES = [
  { label: 'In beta', note: 'Early deployments in Nigeria' },
  { label: 'Naira-first', note: 'Built for local pricing, tax and reporting' },
  { label: 'Multi-branch', note: 'Every location on one stock figure' },
  { label: 'Role-based access', note: 'Each person sees only what they need' },
];

/** The platform. Sold as one system; the suites below are parts of it. */
export const PLATFORM = {
  slug: 'fbs',
  href: '/products/fbs',
  name: 'FBS',
  tab: 'FBS',
  category: 'The platform',
  tagline: 'Sell, hold stock, buy, account and report in one package.',
  summary:
    'FBS is one system rather than a bundle of tools. Point of sale and inventory, procurement and planning, finance and reporting, and marketplace ordering are built together, so they share one stock figure and one ledger, and nothing has to be reconciled between them.',
  bullets: [
    'Point of sale, warehouse and head office reading from one record',
    'Procurement, finance and reporting on the same underlying data',
    'Modular: switch on the suites you need, and change that as you grow',
    'Configured to your industry rather than forcing you into someone else’s process',
  ],
  suites: [
    {
      key: 'pos-inventory',
      name: 'POS & Inventory',
      tagline: 'Sell, count and reorder without the spreadsheet.',
      summary:
        'Real-time inventory and a point of sale that keeps the warehouse, the till and the accounts on the same figure, across every branch you run.',
      points: [
        'Live stock levels across all locations, not a nightly sync',
        'Batch and expiry tracking that flags what to move before it is written off',
        'Cycle counts and variance reconciliation in one click',
        'Advanced reporting your accountant can actually use',
      ],
      modules: ['Flexi Warehouse', 'Flexi Stock Count', 'Flexi POS', 'Last-mile delivery'],
      dashboard: {
        layout: 'till',
        title: 'Till 02 · Ikeja branch',
        meta: '3 items',
        lines: [
          { item: 'Amoxicillin 500mg', qty: '2 × ₦18,400', amount: '₦36,800' },
          { item: 'Paracetamol 1g', qty: '6 × ₦2,150', amount: '₦12,900' },
          { item: 'Glucose IV 500ml', qty: '4 × ₦7,600', amount: '₦30,400' },
        ],
        total: { label: 'Total due', value: '₦80,100' },
        payments: ['Cash', 'Transfer', 'Card'],
        alert: { label: '2 batches expiring', detail: 'within 90 days, flagged at the till' },
      },
    },
    {
      key: 'back-office',
      name: 'Back office',
      tagline: 'Procurement, finance and people on one ledger.',
      summary:
        'Accounting, procurement and planning, warehouse, quality, CRM, assets and HR in one integrated system, configured to your industry rather than forcing you into someone else’s process.',
      points: [
        'Accounts receivable and payable, general ledger and financial modelling',
        'Reorder triggers, demand forecasting, RFQs and supplier quotes',
        'Live KPI reporting pulled from operational data, not re-keyed',
        'Role-based access control throughout',
      ],
      modules: ['Flexi Procurement & Planning', 'Finance & Analytics', 'Flexi CRM & Sales', 'HR & access control', 'Asset management'],
      dashboard: {
        layout: 'ledger',
        title: 'Trial balance (example month)',
        meta: 'Live ledger',
        chips: [
          { label: 'Period', value: 'Open' },
          { label: 'Entries', value: '1,284' },
        ],
        rows: [
          ['121000', 'Accounts receivable', '17,255,000', '-'],
          ['101401', 'Bank (current)', '534,750', '-'],
          ['230100', 'PAYE payable', '-', '47,000'],
          ['230200', 'Pension payable', '-', '104,000'],
          ['410000', 'Service revenue', '-', '17,638,750'],
        ],
        totals: ['', 'Totals', '17,789,750', '17,789,750'],
        balance: { label: 'Debits less credits', value: '0.00' },
      },
    },
    {
      key: 'marketplace',
      name: 'Marketplace',
      tagline: 'Pharmacy procurement, without the phone calls.',
      summary:
        'A B2B marketplace connecting pharmacies with wholesalers: browsing, ordering, batch selection, delivery and returns inside the same system that holds your stock.',
      points: [
        'Pharmacies order from multiple wholesalers with separate carts',
        'Batch-level selection with expiry-aware recommendations',
        'Order workflow, deliveries and returns tracked end to end',
        'Role-based access control for buyers, sellers and finance',
      ],
      modules: ['B2B ordering', 'Batch & expiry', 'Returns', 'Role access', 'Recommendations'],
      dashboard: {
        layout: 'order',
        title: 'Order #48210',
        meta: '3 suppliers',
        suppliers: [
          { name: 'Wholesaler A', items: '18 lines · best expiry Mar 2027', value: '₦642,000', status: { label: 'Confirmed', tone: 'ok' } },
          { name: 'Wholesaler B', items: '12 lines · best expiry Jan 2027', value: '₦418,000', status: { label: 'Confirmed', tone: 'ok' } },
          { name: 'Wholesaler C', items: '7 lines · one batch short', value: '₦224,000', status: { label: 'Partial', tone: 'warn' } },
        ],
        total: { label: 'Order total', value: '₦1,284,000' },
        foot: 'Awaiting approval · returns window opens on delivery',
      },
    },
  ],
  features: [
    {
      title: 'One stock figure',
      body: 'The till, the warehouse and head office read from the same record. A sale updates stock, margin and customer history in the same instant.',
    },
    {
      title: 'Costs that land where they happened',
      body: 'Freight and duty on the receipt, fuel and driver cost on the trip. You can see which products and which routes actually make money.',
    },
    {
      title: 'A month end that is a review, not a rebuild',
      body: 'Counts, variances and adjustments reconcile inside the system, so closing the month is a formality rather than a spreadsheet exercise.',
    },
    {
      title: 'Change the rules without a developer',
      body: 'Modules, permissions, thresholds and workflows are settings you control, not something that needs a developer each time the business changes.',
    },
  ],
  forWho: ['Retail chains & supermarkets', 'Pharmacies & health stores', 'Distributors & wholesalers', 'Manufacturing & services'],
  dashboard: {
    layout: 'overview',
    title: 'Group overview (example data)',
    meta: '4 locations',
    kpis: [
      { label: 'Revenue today', value: '₦4.82M' },
      { label: 'Stock value', value: '₦88.4M' },
      { label: 'Gross margin', value: '31.4%', accent: true },
    ],
    columns: ['Location', 'SKUs', 'Stock value'],
    rows: [
      { cells: ['Ikeja main', '4,180', '₦41.2M'], status: { label: '2 due', tone: 'ok' } },
      { cells: ['Lekki branch', '2,904', '₦29.7M'], status: { label: '7 due', tone: 'warn' } },
      { cells: ['Abuja depot', '1,328', '₦17.5M'], status: { label: '1 due', tone: 'ok' } },
    ],
    bars: [34, 48, 41, 63, 55, 76, 68, 85, 61, 90, 80, 97],
  },
};

/** Systems built for named clients, published with their permission. */
export const CLIENT_BUILDS = [
  {
    slug: 'mergrafav-tms',
    href: '/products/mergrafav-tms',
    name: 'MERGRAFAV GLOBAL TMS',
    client: 'MERGRAFAV GLOBAL',
    category: 'Built for a client · Transport & logistics',
    tagline: 'Cost every trip, and see what each driver did.',
    summary:
      'A transport management system built for MERGRAFAV GLOBAL, covering dispatch through to delivery confirmation, with trip costing and payment workflows attached to the operation rather than kept alongside it.',
    bullets: [
      'Trip management from dispatch through to delivery confirmation',
      'Cost calculation per trip: fuel, maintenance, driver',
      'Payment workflows tied to completed trips',
      'Driver tracking, with vehicle and maintenance records attached',
    ],
    features: [
      { title: 'Trip management, dispatch to delivery', body: 'Every trip has an owner, a route and a confirmation. Nothing closes on a phone call and a guess.' },
      { title: 'Costing per trip', body: 'Fuel, maintenance and driver costs land against the trip that caused them, so you can see which routes make money.' },
      { title: 'Payment workflows', body: 'Funds release against completed trips with an approval trail, instead of cash leaving on trust.' },
      { title: 'Driver and vehicle records', body: 'Licences, documents, maintenance schedules and tracking history attached to the asset and the person operating it.' },
    ],
    forWho: ['Haulage & logistics operators', 'Distribution fleets', 'Last-mile delivery', 'Fleet operators'],
    dashboard: {
      layout: 'trips',
      title: 'Lagos → Ibadan corridor',
      meta: '6 active trips',
      trips: [
        { id: 'TRP-3391', route: 'Lagos → Ibadan', driver: 'A. Bakare', cost: '₦318,400', progress: 100, status: { label: 'Delivered', tone: 'ok' } },
        { id: 'TRP-3392', route: 'Ibadan → Akure', driver: 'M. Okafor', cost: '₦402,100', progress: 62, status: { label: 'In transit', tone: 'ok' } },
        { id: 'TRP-3393', route: 'Lagos → Sagamu', driver: 'S. Adeyemi', cost: '₦287,900', progress: 24, status: { label: 'Pending pay', tone: 'warn' } },
      ],
    },
  },
  {
    slug: 'towa-erp',
    href: '/products/towa-erp',
    name: 'Towa Associates ERP',
    client: 'Towa Associates',
    external: 'https://www.towaassociates.com',
    category: 'Built for a client · Structural engineering',
    tagline: 'Runs the firm’s books, projects and people on one system.',
    summary:
      'A custom ERP built for Towa Associates, a structural-engineering practice in Lagos. It runs the firm’s own accounting, projects and payroll, and is configured for other firms in the same line of work.',
    bullets: [
      'Ledger-first accounting: AR, AP, bank reconciliation, full statements',
      'Projects and timesheets from an engineer’s day through to billing',
      'Payroll with PAYE, pension, NHF, NHIS and staff loans',
      'HR records and role-based permissions across the firm',
    ],
    features: [
      { title: 'Ledger-first double-entry accounting', body: 'Every document posts to a journal, so the balance sheet, profit and loss, cash flow and trial balance are computed from live data rather than assembled by hand.' },
      { title: 'Projects and timesheets', body: 'Engineer time is captured against a project, which is what billing and budget-versus-actual are then built from.' },
      { title: 'Payroll and statutory deductions', body: 'Payroll runs with PAYE, employee pension, NHF, NHIS and staff loan repayments handled as part of the posting, not applied afterwards.' },
      { title: 'Bank statements as the receipts mechanism', body: 'Statement imports match inflows and code outflows, so reconciliation happens against the bank rather than a guess.' },
      { title: 'Permissions as data', body: 'Roles and their permissions are editable, so access can be shaped around how the firm actually staffs its work.' },
      { title: 'Built to be resold', body: 'The same system is configured and deployed for other firms in the sector.' },
    ],
    forWho: ['Structural & civil engineering', 'Consulting practices', 'Project-based professional services', 'Firms needing statutory payroll'],
    dashboard: {
      layout: 'ledger',
      title: 'Trial balance (period to date)',
      meta: 'Live ledger',
      chips: [
        { label: 'Accounts', value: '161' },
        { label: 'Base currency', value: 'NGN' },
      ],
      rows: [
        ['121000', 'Accounts receivable', '17,255,000', '-'],
        ['101401', 'Bank (current)', '534,750', '-'],
        ['230300', 'NHF payable', '-', '16,250'],
        ['230100', 'PAYE payable', '-', '47,000'],
        ['410000', 'Consulting revenue', '-', '17,726,500'],
      ],
      totals: ['', 'Totals', '17,789,750', '17,789,750'],
      balance: { label: 'Difference', value: '0.00' },
    },
  },
];

/** Everything shown under /products, in display order. */
export const PRODUCTS = [PLATFORM, ...CLIENT_BUILDS];

export const PROBLEMS = [
  {
    n: '01',
    title: 'Disconnected tools',
    body: 'Sales in one app. Stock in a notebook. Finance in a spreadsheet. Nobody in the building can see the whole picture at once.',
  },
  {
    n: '02',
    title: 'Stock you can’t trust',
    body: 'Expiry dates missed, phantom quantities, reorders placed on instinct. The cost shows up as write-offs at year end.',
  },
  {
    n: '03',
    title: 'Decisions after the fact',
    body: 'By the time you learn a branch is bleeding, the month is closed. Reporting tells you history, not what to do next.',
  },
];

/**
 * Replaces the old percentage-claim cards. Describes what the system is built
 * to do, no outcome figures we cannot stand behind yet.
 */
export const OUTCOMES = [
  {
    title: 'One stock figure',
    body: 'The till, the warehouse and head office all read from one record.',
  },
  {
    title: 'Costs that land where they happened',
    body: 'Freight on the receipt, fuel and driver cost on the trip.',
  },
  {
    title: 'A month end that is a review',
    body: 'Counts and variances reconcile inside the system, not in a spreadsheet.',
  },
];

export const OUTCOME_CAVEAT =
  'We are in beta. We will publish measured outcomes once we have enough deployments to stand behind them.';

/**
 * Three steps, not five. Fleet movement belongs to MERGRAFAV and reconciliation
 * is covered by the Back office suite, so the homepage keeps the core chain.
 */
export const MOVEMENTS = [
  { n: '01', title: 'Receive', body: 'Purchase orders, goods received and supplier invoices in one record.', caption: 'Reorder triggers fire from actual usage. RFQs go out, supplier quotes come back, and the goods-received note updates stock as it is entered.', metric: 'POs', metricLabel: 'open purchase orders' },
  { n: '02', title: 'Store', body: 'Batch, expiry and multi-location stock held against one figure.', caption: 'Batch numbers, expiry dates and stock across every location against one figure. Nothing lives in a branch notebook that head office cannot see.', metric: 'SKUs', metricLabel: 'tracked per location' },
  { n: '03', title: 'Sell', body: 'Till, orders and customers, updating stock as the sale lands.', caption: 'The till, the orders and the customer record move together. Every sale updates stock, margin and customer history in the same instant.', metric: 'Tills', metricLabel: 'per branch' },
];

export const SERVICES = [
  'Software development',
  'Website & e-commerce',
  'Business email configuration',
  'AI & data services',
  'Integration & automation',
  'Design & UX/UI',
  'Cybersecurity',
  'Digital transformation',
  'IT support & maintenance',
  'Training & documentation',
  'Industry solutions',
  'Startup & MVP builds',
  'Business analytics',
  'Contract stock counting',
  'Training & consulting',
  'Flexi business gadgets',
];

export const TRUST_POINTS = [
  { title: 'Built for African operations', body: 'Multi-location, Naira-first and designed around the way businesses here actually trade.' },
  { title: 'Access control', body: 'Role-based permissions so a cashier, a store manager and a finance lead each see exactly what they should.' },
  { title: 'Hosted for you', body: 'The platform your till depends on runs on managed infrastructure that we maintain for you.' },
  { title: 'Deployment', body: 'We load your stock, train your team and stay reachable after go-live.' },
];

export const PRICING_NOTE =
  'Amounts are quoted per deployment, because the work depends on your size and how much historical data needs migrating. The tiers below show how we work. The platform is in beta, which is reflected in current terms.';

/** Services, grouped the way a buyer thinks about them. */
export const SERVICE_GROUPS = [
  {
    heading: 'Build it',
    summary: 'New software, or the storefront and product experience around it.',
    items: [
      { title: 'Software development', body: 'Custom applications, internal tools and APIs built to your process rather than forcing you into someone else’s.' },
      { title: 'Website & e-commerce', body: 'Marketing sites and online stores that connect to your stock instead of running in parallel to it.' },
      { title: 'Business email configuration', body: 'Company email set up on your own domain, with the records and authentication configured properly so messages land in inboxes rather than spam. Delivered for several clients.' },
      { title: 'Design & UX/UI', body: 'Interfaces your staff can learn in an afternoon, because software nobody uses is a cost, not a system.' },
      { title: 'Startup & MVP builds', body: 'Get a testable version in front of real customers before committing to a full build.' },
    ],
  },
  {
    heading: 'Connect it',
    summary: 'Make the systems you already pay for talk to each other.',
    items: [
      { title: 'Integration & automation', body: 'Payment gateways, accounting packages, banks and third-party platforms wired into one flow.' },
      { title: 'AI & data services', body: 'Demand forecasting and reporting built on your own transaction history, not generic benchmarks.' },
      { title: 'Business analytics', body: 'Dashboards and KPI reporting leadership will actually look at, built from live operational data.' },
    ],
  },
  {
    heading: 'Protect & run it',
    summary: 'Keep it up, keep it secure, keep it supported.',
    items: [
      { title: 'Cybersecurity', body: 'Access control, hardening and review, particularly where payments and stock records are involved.' },
      { title: 'IT support & maintenance', body: 'Ongoing support with a named contact, so problems are not queued behind a ticket system.' },
      { title: 'Digital transformation', body: 'Programme-level work: process review, phased rollout and training for the whole team.' },
    ],
  },
  {
    heading: 'Advise & operate',
    summary: 'Where you want expertise rather than software.',
    items: [
      { title: 'Training & documentation', body: 'Role-based training and written procedures your team can hand to the next hire.' },
      { title: 'Contract stock counting', body: 'Our people count your stock on site, reconcile variance and hand over a signed position.' },
      { title: 'Industry solutions', body: 'Configurations used in retail, pharmacy, distribution and manufacturing.' },
      { title: 'Flexi business gadgets', body: 'Barcode scanners, label printers and hardware that work with the platform out of the box.' },
    ],
  },
];

/** Commercial models. Amounts are quoted, not published. */
export const PRICING_TIERS = [
  {
    name: 'Free trial',
    amount: 'Free',
    term: '14 days, no card required',
    summary: 'The full product, single branch, so you can judge it on your own stock.',
    includes: ['Core modules', 'Single branch', 'Your own stock loaded', 'Email support'],
    cta: { label: 'Start free trial', href: '/contact', variant: 'outline' },
  },
  {
    name: 'Subscription',
    amount: 'Quoted',
    term: 'per branch, billed monthly',
    highlight: true,
    summary: 'The standard route: full suite, all branches, updates and support included.',
    includes: ['Full suite, every branch', 'Updates included', 'Onboarding & training', 'Priority support', 'Flexible term'],
    cta: { label: 'Book a demo', href: '/contact', variant: 'brand' },
  },
  {
    name: 'Lifetime licence',
    amount: 'Quoted',
    term: 'one-off, per deployment',
    summary: 'Own the deployment outright: the right fit when a subscription is commercially awkward.',
    includes: ['Perpetual licence', 'Installed for your business', 'Optional annual maintenance', 'Staff training'],
    cta: { label: 'Talk to sales', href: '/contact', variant: 'outline' },
  },
  {
    name: 'Enterprise',
    amount: 'Custom',
    term: 'group & multi-entity',
    summary: 'For groups running several entities, or where the configuration is genuinely bespoke.',
    includes: ['Industry configuration', 'Custom integrations & API', 'Dedicated support engineer', 'Service-level agreement', 'Data migration'],
    cta: { label: 'Contact sales', href: '/contact', variant: 'ink' },
  },
];

export const REVENUE_MODELS = [
  { title: 'Affiliate partnerships', body: 'Introduce a business that deploys, and earn on the subscription for as long as they stay.' },
  { title: 'Data insights', body: 'Anonymised, aggregated market reporting that helps suppliers plan, never your competitors’ records.' },
];

export const PRICING_FAQS = [
  { q: 'How is a quote actually put together?', a: 'By the number of branches, the modules you switch on and how much historical data needs migrating. We quote after a short demo so the number is real rather than indicative.' },
  { q: 'Can we start small?', a: 'Yes, most deployments start with one location. The platform is modular, and modules you have not enabled cost nothing.' },
  { q: 'What happens after the free trial?', a: 'Nothing automatic. The trial does not roll into a paid plan; we talk first, and you decide.' },
  { q: 'Do you charge for onboarding?', a: 'Onboarding, training and documentation are included in the subscription tiers. Custom integration work and contract stock counting are quoted separately.' },
  { q: 'Is our data ours?', a: 'Yes. Your operational data belongs to your business, and we can export it on request.' },
  { q: 'The platform is in beta. What does that mean for us?', a: 'It means you are deploying early, with direct access to the team building it. We are explicit about this rather than describing it as finished software, and we do not publish performance numbers we cannot yet stand behind.' },
];
